/**
 * @file keys.ts
 * @description Состав палитры темы кабинета, версия 1 (phase19.31, SC1–SC3;
 * SC66 — необязательный ключ `gradient-warm`).
 *
 * Шаблон темы кабинета — данные платформы (`CabinetTheme.tokens`), а не код.
 * Палитра — плоский объект «ключ → цвет», ключ равен имени CSS-переменной без
 * `--`. Состав ключей версионируется (`TOKENS_SCHEMA`): палитра, не прошедшая
 * проверку своей версии, при чтении заменяется на «Commora».
 *
 * Источник состава — docs/phases/phase19.31-materials/db-design.md §1–2;
 * эталон значений — миграция 20261002130000_phase19_31_cabinet_themes.
 * Размеры, шрифты, скругления и цвета статусов в палитру не входят (SC3, SC27).
 */

/** Версия состава ключей палитры. Меняется только вместе с data-миграцией. */
export const TOKENS_SCHEMA = 1;

/** Схема шаблона: какой фиксированный набор статусов применять. */
export type CabinetThemeScheme = "LIGHT" | "DARK";

/**
 * 45 ключей палитры версии 1 — порядок как в проекте данных (группы).
 * 44 обязательных + 1 необязательный (`CABINET_OPTIONAL_PALETTE_KEYS`).
 */
export const CABINET_PALETTE_KEYS_V1 = [
  // Поверхности
  "background",
  "surface-low",
  "surface-lowest",
  "surface-bright",
  "input",
  // Текст
  "on-surface",
  "on-surface-variant",
  "on-surface-muted",
  "on-surface-strong",
  // Действие
  "primary",
  "primary-hover",
  "primary-pressed",
  "on-primary",
  "link",
  "ring",
  // Градиент главного действия
  "gradient-start",
  "gradient-end",
  // Тёплый тон декоративного фона («шёлковые ленты», SC66). Необязательный:
  // в проверки контраста текста не входит (декор, на нём нет текста).
  "gradient-warm",
  // Выбранное и мягкая кнопка
  "accent",
  "accent-foreground",
  "soft-hover",
  "soft-pressed",
  "soft-pressed-fg",
  // Линии и поля
  "border",
  "field-border",
  "field-border-hover",
  "field-border-focus",
  // Таблицы
  "table-head-bg",
  "row-hover",
  "row-selected",
  "row-marker",
  // Боковая панель
  "sidebar",
  "sidebar-foreground",
  "sidebar-muted",
  "sidebar-strong",
  "sidebar-accent",
  "sidebar-accent-foreground",
  "sidebar-hover",
  "sidebar-hover-foreground",
  "sidebar-border",
  "sidebar-edge",
  "sidebar-ring",
  "sidebar-marker",
  // Подсказка
  "tooltip-bg",
  "tooltip-fg",
] as const;

/** Ключ палитры версии 1. */
export type CabinetPaletteKey = (typeof CABINET_PALETTE_KEYS_V1)[number];

/**
 * Необязательные ключи версии 1 — добавлены в состав позже стартовых палитр
 * (расширение без смены `TOKENS_SCHEMA`): палитра без такого ключа
 * проверку проходит (кабинет подставляет запасное значение, для
 * `gradient-warm` — `gradient-end`); если ключ есть — действует проверка
 * формата цвета. Смена TOKENS_SCHEMA сделала бы все сохранённые палитры
 * «чужой версии» до data-миграции — поэтому так.
 *  - `gradient-warm` — SC66, data-миграция phase19_31_cabinet_theme_gradient_warm.
 */
export const CABINET_OPTIONAL_PALETTE_KEYS = ["gradient-warm"] as const;

/** Необязательный ключ палитры версии 1. */
export type CabinetOptionalPaletteKey = (typeof CABINET_OPTIONAL_PALETTE_KEYS)[number];

/** Обязательный ключ палитры версии 1. */
export type CabinetRequiredPaletteKey = Exclude<CabinetPaletteKey, CabinetOptionalPaletteKey>;

/**
 * 44 обязательных ключа версии 1 (состав без необязательных) — для проверок
 * «палитра полная» у потребителей (кэш палитры в кабинете и т. п.).
 */
export const CABINET_REQUIRED_PALETTE_KEYS_V1: readonly CabinetRequiredPaletteKey[] =
  CABINET_PALETTE_KEYS_V1.filter(
    (key): key is CabinetRequiredPaletteKey =>
      !(CABINET_OPTIONAL_PALETTE_KEYS as readonly string[]).includes(key),
  );

/** Быстрая проверка «ключ необязательный». */
export function isCabinetOptionalPaletteKey(key: string): key is CabinetOptionalPaletteKey {
  return (CABINET_OPTIONAL_PALETTE_KEYS as readonly string[]).includes(key);
}

/**
 * Палитра версии 1, прошедшая проверку: каждому обязательному ключу — цвет
 * `#rrggbb` или `#rrggbbaa`; необязательные могут отсутствовать (палитры,
 * сохранённые до их появления).
 */
export type CabinetPalette = Readonly<
  Record<CabinetRequiredPaletteKey, string> & Partial<Record<CabinetOptionalPaletteKey, string>>
>;

/** Полная палитра заводского шаблона: все 45 ключей, включая необязательные. */
export type CabinetFullPalette = Readonly<Record<CabinetPaletteKey, string>>;

/**
 * Формат цвета палитры. Только hex: прозрачность — восьмизначным hex вместо
 * `rgba()`, чтобы через ключ палитры нельзя было подставить в CSS ничего,
 * кроме цвета (значения уходят в `style` кабинета как CSS-переменные).
 */
export const CABINET_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** Быстрая проверка «ключ входит в состав версии 1». */
export function isCabinetPaletteKey(key: string): key is CabinetPaletteKey {
  return (CABINET_PALETTE_KEYS_V1 as readonly string[]).includes(key);
}

/**
 * Цвета схемы, которые НЕ входят в палитру шаблона, но участвуют в проверке
 * контраста (смешанные пары db-design §3): текстовые цвета ошибки, успеха и
 * предупреждения на карточке. Общие для всех шаблонов одной схемы (SC27);
 * значения — commora-theme.css (светлая) и theme-palettes.md §3 (тёмная).
 */
export const CABINET_SCHEME_COLORS: Readonly<
  Record<CabinetThemeScheme, Readonly<Record<"destructive-text" | "success" | "warning", string>>>
> = {
  LIGHT: {
    "destructive-text": "#b91c1c",
    success: "#047857",
    warning: "#b45309",
  },
  DARK: {
    "destructive-text": "#f87171",
    success: "#34d399",
    warning: "#fbbf24",
  },
};

/** Ключ цвета схемы (вне палитры). */
export type CabinetSchemeColorKey = keyof (typeof CABINET_SCHEME_COLORS)["LIGHT"];
